import { TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';
import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { trackTouringEvent } from '../analytics';

export type ClickStreamMetadata = {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
};

const mapTourConfirmationTypeToClickstreamObjectName = (tourConfirmationType: TourConfirmationType) => {
  switch (tourConfirmationType) {
    case TourConfirmationType.INSTANT:
      return 'instant_tour';
    case TourConfirmationType.LANDLORD_CONFIRM:
      return 'review_and_confirm';
    default:
      return '';
  }
};

// https://dataportal.zillowgroup.net/event-registry/6637
export const emitTourTypeSelectionEvent = (
  { currentProperty, user }: ClickStreamMetadata,
  tourConfirmationType: TourConfirmationType,
) =>
  trackTouringEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '6637',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_tour_scheduler',
      trigger_type_nm: 'interaction',
      trigger_object_nm: mapTourConfirmationTypeToClickstreamObjectName(tourConfirmationType),
      trigger_source_nm: 'pick_tour_type_next',
    },
    semantic: {
      semantic_event_nm: 'tour_scheduling_tour_type',
      topic_tag_txt: ['zrm_tour_scheduling', 'zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

export const CALENDAR_CLICKSTREAM_ACTION = {
  ADD_TO_LISTING_CTA: 'add_to_listing_cta',
  ADD_TOUR_EVENT: 'add_tour_event',
  SAVE_TOUR_EVENT: 'save_tour_event',
  EDIT_TOUR: 'edit_tour',
  ADD_TOUR_MOUSE_CLICK: 'add_tour_mouse_click',
  DELETE_TOUR: 'delete_tour',
  CONFIRM_TOUR: 'confirm_tour',
  DECLINE_TOUR: 'decline_tour',
  CANCEL_TOUR: 'cancel_tour',
} as const;
export type CALENDAR_CLICKSTREAM_ACTION =
  (typeof CALENDAR_CLICKSTREAM_ACTION)[keyof typeof CALENDAR_CLICKSTREAM_ACTION];

// https://dataportal.zillowgroup.net/event-registry/7121
export const emitCalendarEventActionEvent = (
  { currentProperty, user }: ClickStreamMetadata,
  calendarActionType: CALENDAR_CLICKSTREAM_ACTION,
) =>
  trackTouringEvent({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: '7121',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_tour_scheduler',
      trigger_type_nm: 'interaction',
      trigger_object_nm: calendarActionType,
      trigger_source_nm: 'tour_event',
    },
    semantic: {
      semantic_event_nm: 'tour_scheduling_add_event',
      topic_tag_txt: ['zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const TourConfirmationTypeClickStreamSourceMap = {
  [TourConfirmationType.INSTANT]: 'instant_book',
  [TourConfirmationType.LANDLORD_CONFIRM]: 'review_confirm',
};
// https://dataportal.zillowgroup.net/event-registry/7126
export const emitCalendarSettingsUpdateConfirmationTypeEvent = (
  { currentProperty, user }: ClickStreamMetadata,
  tourConfirmationType: TourConfirmationType,
) =>
  trackTouringEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7126',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_tour_scheduler_settings',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zrm_tour_setting_save',
      trigger_source_nm: TourConfirmationTypeClickStreamSourceMap[tourConfirmationType],
    },
    semantic: {
      semantic_event_nm: 'tour_scheduling_add_event',
      topic_tag_txt: ['zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

export const TourAvailabilityWizardImpresssionSourceMap = {
  PICK_TOUR_TYPE_NEXT: 'pick_tour_type_next',
  PICK_TOUR_TIME_NEXT: 'pick_tour_time_next',
  TOUR_EVENT: 'tour_event',
} as const;
export type TourAvailabilityWizardImpresssionSourceMap =
  (typeof TourAvailabilityWizardImpresssionSourceMap)[keyof typeof TourAvailabilityWizardImpresssionSourceMap];

// https://dataportal.zillowgroup.net/event-registry/7540
export const emitTourAvailabilityWizardImpresssionEvent = (
  { currentProperty, user }: ClickStreamMetadata,
  source: TourAvailabilityWizardImpresssionSourceMap,
) =>
  trackTouringEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7540',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_tour_scheduler',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: source,
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7473
export const emitTourAvailabilityWizardTimeZoneNextClickEvent = ({ currentProperty, user }: ClickStreamMetadata) =>
  trackTouringEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7473',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_tour_scheduler',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'select_time_zone',
      trigger_source_nm: 'pick_tour_time_next',
    },
    semantic: {
      semantic_event_nm: 'zrm_tour_time_zone',
      topic_tag_txt: ['zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7766
export const emitTourCalendarSettingToggleTourSchedulingEvent = ({ currentProperty, user }: ClickStreamMetadata) =>
  trackTouringEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7766',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_tour_scheduler_settings',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'tour_scheduling_toggle_on_off',
    },
    semantic: {
      semantic_event_nm: 'tour_scheduling_settings_toggle',
      topic_tag_txt: ['zrm_tour_scheduling'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
